import {RefObject, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {DeviceType} from '../incase-domain/common/DeviceType.enum';
import {IElementHtml} from '../incase-domain/elements/html';
import {convertToEm} from '../incase-domain/utils/emConverter';
import {selectedViewSelector} from '../store/selectors/presentation.selectors';

interface UseHtmlResult {
    containerRef: RefObject<HTMLDivElement>;
}

export function useHtmlIframe(element: IElementHtml): UseHtmlResult {
    const {text} = element;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const selectedView = useSelector(selectedViewSelector);

    useEffect(() => {
        if (!text || !containerRef.current) return undefined;

        if (!iframeRef.current) {
            const iframe = document.createElement('iframe');
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.style.overflow = 'auto';

            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(iframe);
            iframeRef.current = iframe;
        }

        const processedText = convertToEm(text);

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

        if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(processedText);
            iframeDoc.close();
        }

        return undefined;
    }, [text]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe && selectedView === DeviceType.MOBILE) {
            iframe.addEventListener('load', () => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });

            const resize = new ResizeObserver(() => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });
            resize.observe(document.body);
        }
    }, [selectedView]);

    return {containerRef};
}
